import { keepPreviousData, QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";

import { getSearchSerialization } from "@garimpa/lib/searchSerialization";

import { AppError } from "./components/app-error";
import { routeTree } from "./routeTree.gen";
import { QUERY_RETRY_POLICY, QUERY_STALE_TIMES, getRetryDelay } from "./utils/query-policies";

const { parseSearch, stringifySearch } = getSearchSerialization();

/**
 * Build a fresh QueryClient per request. Mirrors the policy previously wired
 * in `utils/orpc.client.ts` so SSR and the browser share the same defaults.
 */
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: QUERY_STALE_TIMES.default,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchOnMount: false,
        placeholderData: keepPreviousData,
        retry: QUERY_RETRY_POLICY.attempts,
        retryDelay: getRetryDelay,
      },
    },
  });
}

export function getRouter() {
  const queryClient = makeQueryClient();
  const router = createRouter({
    routeTree,
    context: { queryClient },
    // URLSearchParams search (de)serialization across every route: repeated
    // flat params plus dot paths for nested state (see ./lib/search-serialization).
    parseSearch,
    stringifySearch,
    // Prefetch disabled: grid/list pages can render 100+ release cards, and
    // hover-prefetch would fire N × (root beforeLoad + _app guard + target
    // route loader) RPCs — tens of requests per mouse pass with no meaningful
    // UX win. Rely on the route loader to prefetch on click.
    defaultPreload: false,
    scrollRestoration: true,
    // TanStack Router catches loader/beforeLoad errors and renders them through
    // the closest `errorComponent`, treating the response as a successful
    // render — so by default nothing reaches stderr or the dev log. `AppError`
    // both renders the "A Agulha Pulou" 500 UI and logs the error.
    defaultErrorComponent: AppError,
  });

  // Wires up SSR dehydration/hydration + streaming between router and query.
  // `wrapQueryClient: false` so `__root.tsx` owns the provider tree layout
  // (QueryClientProvider must sit outside ThemeProvider/IntlProvider/Nuqs).
  setupRouterSsrQueryIntegration({
    router,
    queryClient,
    wrapQueryClient: false,
  });

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
